import { useMemo } from 'react'

import { useGetOrganizationUuid } from 'common'
import { getDashboardEventIdForInvitees } from 'components/meetings/utils/get_dashboard_event_id_for_invitees'
import { useGetMeetingSupergraphQuery } from 'shared/api/meetings'

import { useGetMeetingQuery } from '../api'
import { normalizeEventGuestEmails } from '../normalize_event_guest_emails'
import { mapNodeToMeeting } from '../utils'

export const usePrimaryCalendlyMeetingDetail = ({
  shouldUseSupergraphMeetingDetail,
  shouldFetchMeetingDetailInProvider,
  eventId,
  selectedView,
  scopeType,
  scopeId,
  isCalendlyMeeting,
  detailPollingConfig,
  cacheMeetingData,
  timezone,
  hasMsTeamsFlag,
}) => {
  const organizationUuid = useGetOrganizationUuid()
  const shouldFetchSupergraphApi = Boolean(shouldFetchMeetingDetailInProvider && shouldUseSupergraphMeetingDetail)
  const shouldCallScheduledEventsRest = Boolean(
    shouldFetchMeetingDetailInProvider && isCalendlyMeeting
  )

  const restMeetingQueryArgs = useMemo(() => {
    if (!shouldCallScheduledEventsRest || !scopeType) {
      return { eventId: undefined, selectedView: undefined, scopeType: undefined, scopeId: undefined }
    }
    return { eventId, selectedView, scopeType, scopeId }
  }, [shouldCallScheduledEventsRest, eventId, scopeType, scopeId, selectedView])

  const {
    data: restMeetingData,
    isError: restApiError,
    isSuccess: restApiSuccess,
    error: restApiErrorPayload,
  } = useGetMeetingQuery(restMeetingQueryArgs, {
    skip: !shouldCallScheduledEventsRest || !scopeType,
    refetchOnMountOrArgChange: true,
    ...detailPollingConfig,
  })

  const {
    data: supergraphMeetingData,
    isError: supergraphError,
    isSuccess: supergraphSuccess,
    error: supergraphErrorPayload,
  } = useGetMeetingSupergraphQuery(
    { id: eventId, organizationUuid: organizationUuid || '' },
    {
      skip: !shouldFetchSupergraphApi || !eventId || !organizationUuid,
      refetchOnMountOrArgChange: true,
      ...detailPollingConfig,
    }
  )

  const primaryMeetingData = useMemo(() => {
    if (!shouldUseSupergraphMeetingDetail) {
      if (!restMeetingData) return undefined
      return {
        ...restMeetingData,
        eventGuestEmails: normalizeEventGuestEmails(restMeetingData),
      }
    }
    if (!supergraphMeetingData?.uuid || supergraphMeetingData.uuid !== eventId) {
      return undefined
    }
    let mapped = mapNodeToMeeting(supergraphMeetingData, timezone || 'UTC', hasMsTeamsFlag)
    if (restMeetingData) {
      const dash = getDashboardEventIdForInvitees(restMeetingData)
      mapped = { ...mapped, inviteesDashEventId: dash }
      const isNumericDash = typeof dash === 'number'
        || (typeof dash === 'string' && /^\d+$/.test(dash) && dash.length < 20)
      if (isNumericDash) {
        mapped = { ...mapped, id: dash }
      }
      // Supergraph `getMeetingById` does not query guest emails; REST scheduled-events detail does.
      const fromRest = normalizeEventGuestEmails(restMeetingData)
      const fromMapped = normalizeEventGuestEmails(mapped)
      if (fromRest.length > 0 && fromMapped.length === 0) {
        mapped = { ...mapped, eventGuestEmails: fromRest }
      }
    }
    const cacheId = cacheMeetingData?.id
    if (cacheId != null && cacheId !== '' && (mapped.id == null || mapped.id === '')) {
      mapped = { ...mapped, id: cacheId }
    }
    const cacheMi = cacheMeetingData?.meetingIntelligence
    const detailMi = mapped.meetingIntelligence
    if (cacheMi && detailMi && typeof cacheMi === 'object' && typeof detailMi === 'object') {
      return {
        ...mapped,
        meetingIntelligence: { ...cacheMi, ...detailMi },
      }
    }
    return mapped
  }, [
    shouldUseSupergraphMeetingDetail,
    supergraphMeetingData,
    restMeetingData,
    cacheMeetingData,
    eventId,
    timezone,
    hasMsTeamsFlag,
  ])

  return {
    primaryMeetingData,
    primaryApiError: shouldUseSupergraphMeetingDetail ? supergraphError : restApiError,
    primaryApiSuccess: shouldUseSupergraphMeetingDetail ? supergraphSuccess : restApiSuccess,
    primaryApiErrorPayload: shouldUseSupergraphMeetingDetail ? supergraphErrorPayload : restApiErrorPayload,
  }
}
